import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import ErrorModal from "../components/Error/Error";
import Loading from "../components/Loading/Loading";
import {
  BirlikteGuzellestirelimService,
  ProjelerService,
  TemaService,
} from "../services";
import { AnasayfaService } from "../services/anasayfa.service";
import { HakkimizdaService } from "../services/hakkimizda.service";
import { IletisimService } from "../services/iletisim.service";
import {
  AnasayfaItem,
  HakkimizdaBaslik,
  HakkimizdaGorsel,
  IletisimBilgileri,
  Proje,
  Tema,
  Workshop,
} from "../services/types";

interface DataContextType {
  homePageData: AnasayfaItem[];
  hakkimizdaBasliklar: HakkimizdaBaslik[];
  hakkimizdaGorsel: HakkimizdaGorsel | null;
  iletisimBilgileri: IletisimBilgileri | null;
  tema: Tema[];
  projeler: Proje[];
  workshoplar: Workshop[];
  isLoading: boolean;
  error: Error | null;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider = ({ children }: { children: ReactNode }) => {
  const [homePageData, setHomePageData] = useState<AnasayfaItem[]>([]);
  const [hakkimizdaBasliklar, setHakkimizdaBasliklar] = useState<
    HakkimizdaBaslik[]
  >([]);
  const [hakkimizdaGorsel, setHakkimizdaGorsel] =
    useState<HakkimizdaGorsel | null>(null);
  const [iletisimBilgileri, setIletisimBilgileri] =
    useState<IletisimBilgileri | null>(null);
  const [tema, setTema] = useState<Tema[]>([]);
  const [projeler, setProjeler] = useState<Proje[]>([]);
  const [workshoplar, setWorkshoplar] = useState<Workshop[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [
          anasayfaResponse,
          hakkimizdaBaslikResponse,
          hakkimizdaGorselResponse,
          iletisimResponse,
          temaResponse,
          projelerResponse,
          workshopResponse,
        ] = await Promise.all([
          AnasayfaService.gorselListele(),
          HakkimizdaService.baslikListele(),
          HakkimizdaService.gorselGetir(),
          IletisimService.iletisimBilgileriGetir(),
          TemaService.listele(),
          ProjelerService.listele(),
          BirlikteGuzellestirelimService.workshopListele(),
        ]);

        setHomePageData(anasayfaResponse.data);
        setHakkimizdaBasliklar(hakkimizdaBaslikResponse.data);
        setHakkimizdaGorsel(hakkimizdaGorselResponse.data[0] || null);
        setIletisimBilgileri(iletisimResponse.data[0] || null);
        setTema(temaResponse.data);
        setProjeler(projelerResponse.data);
        setWorkshoplar(workshopResponse.data);
      } catch (err) {
        console.log(err);
        setError(err instanceof Error ? err : new Error("Veri yükleme hatası"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const value = {
    homePageData,
    hakkimizdaBasliklar,
    hakkimizdaGorsel,
    iletisimBilgileri,
    tema,
    projeler,
    workshoplar,
    isLoading,
    error,
  };

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorModal message={error.message} />;
  }

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};
